import { useMemo, useState, useRef, useEffect, createContext, useContext } from "react";
import { Form, FormField as BaseFormField, PrimaryButton, SecondaryButton } from "common/Forms";
import { InRow } from "common/Grid";
import { TextInput, SelectInput, PasswordInput } from "common/Forms/inputs";
import { Grid, GridCell } from "common/Grid/Grid";
import { useNavigate } from "react-router-dom";
import { staffRoleOptions, STAFF_ROLES } from "Constant/Constant";
import { parseStaffUserErrors } from "./staffUser.schema";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";
import { useGetStaffStoresQuery } from "store/action/staffUserApi";

const FormErrorContext = createContext({});

const FormField = (props) => {
  const errors = useContext(FormErrorContext);
  const errorKey = Object.keys(errors).find(
    (k) =>
      k.split(".").pop() === props.id ||
      k.split(".").pop().toLowerCase() === props.id?.toLowerCase() ||
      k === props.id,
  );
  const { inputFactory, ...rest } = props;
  const wrappedInputFactory = (id, name, onValidation) => (
    <div className="text-black">{inputFactory(id, name, onValidation)}</div>
  );
  return (
    <BaseFormField
      {...rest}
      inputFactory={wrappedInputFactory}
      errorMessage={errorKey ? errors[errorKey] : undefined}
    />
  );
};

const emptyObj = {};
const trimStringValues = (values) =>
  Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      typeof value === "string" ? value.trim() : value,
    ]),
  );

// Parse a full phone string like "+61412345678" back into { prefix, number }
const parsePhoneNumber = (fullPhone, countryOptions) => {
  if (!fullPhone) return { prefix: "+61", number: "" };
  const sorted = [...countryOptions].sort((a, b) => b.dialCode.length - a.dialCode.length);
  for (const c of sorted) {
    if (fullPhone.startsWith(c.dialCode)) {
      return { prefix: c.dialCode, number: fullPhone.slice(c.dialCode.length) };
    }
  }
  return { prefix: "+61", number: fullPhone };
};

const getFlagEmoji = (countryCode) =>
  countryCode
    .toUpperCase()
    .split("")
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join("");

export const StaffUserForm = ({
  defaultValues = emptyObj,
  onSubmit,
  isLoading,
  isEdit = false,
}) => {
  const navigate = useNavigate();

  // ── Country options (computed once) ──
  const countryOptions = useMemo(
    () =>
      getCountries()
        .map((country) => ({
          code: country,
          name: en[country] || country,
          dialCode: `+${getCountryCallingCode(country)}`,
          flag: getFlagEmoji(country),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  // ── Parse default phone on edit ──
  const defaultPhone = useMemo(
    () => parsePhoneNumber(defaultValues.phoneNumber || "", countryOptions),
    [],
  );

  const [userInput, setUserInput] = useState({
    fullName: defaultValues.fullName || "",
    email: defaultValues.email || "",
    phoneNumber: defaultPhone.number,
    phonePrefix: defaultPhone.prefix,
    role: defaultValues.role || "",
    username: defaultValues.username || "",
    pin: defaultValues.pin || "",
    password: "",
    siteid: defaultValues.siteid || "",
  });

  // ── Country prefix dropdown state ──
  const [prefixOpen, setPrefixOpen] = useState(false);
  const [prefixSearch, setPrefixSearch] = useState("");
  const prefixRef = useRef(null);
  const prefixSearchRef = useRef(null);

  useEffect(() => {
    if (!prefixOpen) return;
    const handler = (e) => {
      if (prefixRef.current && !prefixRef.current.contains(e.target)) {
        setPrefixOpen(false);
        setPrefixSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [prefixOpen]);

  useEffect(() => {
    if (prefixOpen) {
      requestAnimationFrame(() => prefixSearchRef.current?.focus());
    }
  }, [prefixOpen]);

  const filteredPrefixOptions = useMemo(() => {
    if (!prefixSearch.trim()) return countryOptions;
    const q = prefixSearch.toLowerCase();
    return countryOptions.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dialCode.includes(q),
    );
  }, [prefixSearch, countryOptions]);

  const selectedCountry = useMemo(
    () => countryOptions.find((c) => c.dialCode === userInput.phonePrefix) || null,
    [userInput.phonePrefix, countryOptions],
  );

  // ── Stores dropdown ──
  const { data: storesResponse } = useGetStaffStoresQuery(
    { staffRole: userInput.role },
    { skip: !userInput.role },
  );
  const storeOptions = useMemo(
    () =>
      (storesResponse?.data || []).map((s) => ({
        value: s.siteid,
        label: s.siteName ? `${s.siteid} - ${s.siteName}` : s.siteid,
      })),
    [storesResponse],
  );

  // ── Validation ──
  const [isSubmitted, setIsSubmitted] = useState(false);

  const formErrors = useMemo(
    () => (isSubmitted ? parseStaffUserErrors(userInput, isEdit) : {}),
    [userInput, isSubmitted, isEdit],
  );

  const handleValidatedSubmit = () => {
    setIsSubmitted(true);
    const trimmedUserInput = trimStringValues(userInput);
    setUserInput(trimmedUserInput);
    const errors = parseStaffUserErrors(trimmedUserInput, isEdit);
    if (Object.keys(errors).length > 0) return;
    onSubmit(trimmedUserInput);
  };

  const textInputBoxClass = ["text_input_box"];

  return (
    <FormErrorContext.Provider value={formErrors}>
      <Form classNames={["my-6"]}>
        <Grid columns={2} rowGap={1.5} columnGap={2} classNames={["!gap-x-3 !gap-y-3"]}>

          {/* Full Name */}
          <GridCell>
            <FormField
              id="fullName"
              label="Full Name"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter Full Name"
                  value={userInput.fullName}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, fullName: val ?? "" }))}
                />
              )}
            />
          </GridCell>

          {/* Email */}
          <GridCell>
            <FormField
              id="email"
              label="Email"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter Email"
                  value={userInput.email}
                  disabled={isEdit && !!defaultValues.email}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, email: val ?? "" }))}
                />
              )}
            />
          </GridCell>

          {/* Username */}
          <GridCell>
            <FormField
              id="username"
              label="Username"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter Username"
                  value={userInput.username}
                  disabled={isEdit && !!defaultValues.username}
                  onChange={(_, val) =>
                    setUserInput((prev) => ({ ...prev, username: (val ?? "").replace(/\s/g, "") }))
                  }
                />
              )}
            />
          </GridCell>

          {/* Phone Number with prefix dropdown */}
          <GridCell>
            <FormField
              id="phoneNumber"
              label="Phone Number"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <div
                  style={{ display: "flex", alignItems: "stretch", width: "100%", position: "relative" }}
                  ref={prefixRef}
                >
                  {/* Prefix trigger */}
                  <button
                    type="button"
                    id="phonePrefix"
                    disabled={isEdit && !!defaultValues.phoneNumber}
                    onClick={() => { setPrefixOpen((o) => !o); setPrefixSearch(""); }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      flexShrink: 0,
                      height: "39px",
                      padding: "0 10px",
                      border: "2px solid #e2e8f0",
                      borderRight: "none",
                      borderRadius: "4px 0 0 4px",
                      background: "#f8fafc",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#374151",
                      whiteSpace: "nowrap",
                      minWidth: "80px",
                      outline: "none",
                      userSelect: "none",
                    }}
                  >
                    <span style={{ fontSize: "18px", lineHeight: 1 }}>
                      {selectedCountry ? selectedCountry.flag : "🌐"}
                    </span>
                    <span style={{ fontSize: "13px" }}>
                      {selectedCountry ? selectedCountry.dialCode : "Code"}
                    </span>
                    <span style={{ fontSize: "9px", color: "#94a3b8", marginLeft: "2px" }}>
                      {prefixOpen ? "▲" : "▼"}
                    </span>
                  </button>

                  {/* Phone digits input */}
                  <div style={{ flex: 1, overflow: "hidden", borderRadius: "0 4px 4px 0" }}>
                    <TextInput
                      id={id}
                      name={name}
                      placeholder="Enter Phone Number"
                      value={userInput.phoneNumber}
                      disabled={isEdit && !!defaultValues.phoneNumber}
                      onChange={(_, val) => {
                        const digits = val.replace(/\D/g, "");
                        setUserInput((prev) => ({ ...prev, phoneNumber: digits }));
                      }}
                    />
                  </div>

                  {/* Country dropdown panel */}
                  {prefixOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        zIndex: 1000,
                        background: "#fff",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: "8px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        width: "300px",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                    >
                      <div style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                        <input
                          ref={prefixSearchRef}
                          type="text"
                          placeholder="Search country or code…"
                          value={prefixSearch}
                          onChange={(e) => setPrefixSearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          style={{
                            width: "100%",
                            padding: "6px 10px",
                            border: "1.5px solid #e2e8f0",
                            borderRadius: "6px",
                            fontSize: "13px",
                            outline: "none",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                      <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                        {filteredPrefixOptions.length === 0 ? (
                          <div style={{ padding: "12px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                            No country found
                          </div>
                        ) : (
                          filteredPrefixOptions.map((c) => (
                            <button
                              key={c.code}
                              type="button"
                              onClick={() => {
                                setUserInput((prev) => ({ ...prev, phonePrefix: c.dialCode }));
                                setPrefixOpen(false);
                                setPrefixSearch("");
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                width: "100%",
                                padding: "8px 12px",
                                border: "none",
                                background:
                                  selectedCountry?.code === c.code ? "#f0f9ff" : "transparent",
                                cursor: "pointer",
                                fontSize: "13px",
                                textAlign: "left",
                                color: "#374151",
                              }}
                              onMouseEnter={(e) => {
                                if (selectedCountry?.code !== c.code)
                                  e.currentTarget.style.background = "#f8fafc";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                  selectedCountry?.code === c.code ? "#f0f9ff" : "transparent";
                              }}
                            >
                              <span style={{ fontSize: "18px", lineHeight: 1, flexShrink: 0 }}>
                                {c.flag}
                              </span>
                              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {c.name}
                              </span>
                              <span style={{ color: "#64748b", fontWeight: 600, flexShrink: 0 }}>
                                {c.dialCode}
                              </span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            />
          </GridCell>

          {/* Role */}
          <GridCell>
            <FormField
              id="role"
              label="Role"
              required={true}
              inputFactory={(id, name) => (
                <SelectInput
                  id={id}
                  name={name}
                  placeholder="Select Role"
                  selectedKey={userInput.role}
                  optionProvider={() => staffRoleOptions}
                  keySelector={(x) => `${x.value}`}
                  isClearable={true}
                  labelSelector={(x) => `${x.label}`}
                  disabled={isEdit && !!defaultValues.role}
                  onChange={(_, n) => {
                    const newRole = n ? n.value : "";
                    setUserInput((prev) => ({
                      ...prev,
                      role: newRole,
                      siteid: "",
                    }));
                  }}
                />
              )}
            />
          </GridCell>

          {/* PIN */}
          <GridCell>
            <FormField
              id="pin"
              label="PIN (4 digits)"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter 4-digit PIN"
                  value={userInput.pin}
                  disabled={isEdit && !!defaultValues.pin}
                  onChange={(_, val) => {
                    const digits = val.replace(/\D/g, "").slice(0, 4);
                    setUserInput((prev) => ({ ...prev, pin: digits }));
                  }}
                />
              )}
            />
          </GridCell>

          {/* Password — hidden on edit */}
          {!isEdit && (
            <GridCell>
              <FormField
                id="password"
                label="Password"
                required={true}
                classNames={textInputBoxClass}
                inputFactory={(id, name) => (
                  <PasswordInput
                    id={id}
                    name={name}
                    placeholder={"Enter Password"}
                    value={userInput.password}
                    onChange={(_, val) => setUserInput((prev) => ({ ...prev, password: val }))}
                  />
                )}
              />
            </GridCell>
          )}

          {/* Site ID — hidden for Staff Super Admin */}
          {userInput.role !== STAFF_ROLES.STAFF_SUPER_ADMIN && (
            <GridCell>
              <FormField
                id="siteid"
                label="Site"
                required={true}
                inputFactory={(id, name) => (
                  <SelectInput
                    id={id}
                    name={name}
                    placeholder={userInput.role ? "Select Site" : "Select a role first"}
                    selectedKey={userInput.siteid}
                    optionProvider={() => storeOptions}
                    keySelector={(x) => x.value}
                    labelSelector={(x) => x.label}
                    isClearable={true}
                    isDisabled={!userInput.role}
                    disabled={isEdit && !!defaultValues.siteid}
                    onChange={(_, n) =>
                      setUserInput((prev) => ({ ...prev, siteid: n ? n.value : "" }))
                    }
                  />
                )}
              />
            </GridCell>
          )}

        </Grid>

        <InRow gap={1} classNames={["justify-center mt-8"]}>
          <PrimaryButton
            classNames={["min-w-[180px]"]}
            disabled={isLoading}
            action={handleValidatedSubmit}>
            {isEdit ? "Update Staff User" : "Create Staff User"}
          </PrimaryButton>
          <SecondaryButton classNames={["min-w-[180px]"]} action={() => navigate("/staff-user")}>
            Cancel
          </SecondaryButton>
        </InRow>
      </Form>
    </FormErrorContext.Provider>
  );
};
